import dynamic from "next/dynamic";

import { useMemo } from "react";

import { Loader } from "components/customElements/loader";
import { colors } from "consts";

import ToggleTimeScope from "./ToggleTimeScope";
import useFinancialData from "./useFinancialData";

const TimeSeriesChart = dynamic(
  () => import("components/AmChart/TimeSeriesChart"),
  {
    ssr: false,
  }
);

const TRChartConfig = {
  title: "Turnover Ratios",
  charts: [
    {
      type: "line",
      name: "Receivables Turnover",
      fieldName: "receivables_turnover",
      color: colors.smartTeal,
    },
    {
      type: "line",
      name: "Inventory Turnover",
      fieldName: "inventory_turnover",
      color: colors.grass,
    },
    {
      type: "line",
      name: "Payables Turnover",
      fieldName: "payables_turnover",
      color: colors.mellowYellow,
    },
  ],
  isLineValuePercent: false,
};

const CCRChartConfig = {
  title: "Cash Conversion Ratios",
  charts: [
    {
      type: "line",
      name: "DSO",
      fieldName: "dso",
      color: colors.smartTeal,
    },
    {
      type: "line",
      name: "DIO",
      fieldName: "dio",
      color: colors.grass,
    },
    {
      type: "line",
      name: "DPO",
      fieldName: "dpo",
      color: colors.mellowYellow,
    },
    {
      type: "line",
      name: "Cash Conversion Cycle",
      fieldName: "cash_conversion_cycle",
      color: colors.jade,
    },
  ],
  isLineValuePercent: false,
};

interface Props {
  entity: any;
}

const WorkingCapital = ({ entity }: Props) => {
  const { timeScopeData: chartData, isLoading } = useFinancialData(
    entity?.slug || ""
  );

  const hasTRData = useMemo(
    () =>
      chartData?.some(
        (item) =>
          !!item?.receivables_turnover ||
          !!item?.inventory_turnover ||
          !!item?.payables_turnover
      ),
    [chartData]
  );

  const hasCCRData = useMemo(
    () =>
      chartData?.some(
        (item) =>
          !!item?.receivables_turnover ||
          !!item?.inventory_turnover ||
          !!item?.payables_turnover
      ),
    [chartData]
  );

  if (isLoading) return <Loader />;

  const chartClass = "w-full h-[400px] mb-[80px]";
  return (
    <>
      <ToggleTimeScope />
      {hasTRData ? (
        <div className={chartClass}>
          <TimeSeriesChart
            chartData={chartData}
            chartConfig={TRChartConfig}
            className={chartClass}
          />
        </div>
      ) : null}
      {hasCCRData ? (
        <div className={chartClass}>
          <TimeSeriesChart
            chartData={chartData}
            chartConfig={CCRChartConfig}
            className={chartClass}
          />
        </div>
      ) : null}
    </>
  );
};

export default WorkingCapital;
